/**
 * Product Management - Admin UI for managing global products
 * 
 * Features:
 * - CRUD operations for products
 * - Workflow type assignment (TAX_SEARCH, TITLE_SEARCH, etc.)
 * - Category assignment
 * - Default price configuration
 * - Soft delete with usage checks
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import { 
  Plus, Edit2, Trash2, Package, Search, Filter, RefreshCw,
  CheckCircle2, XCircle, AlertTriangle, DollarSign, Tag, Layers,
  ArrowLeft, DatabaseZap, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { useAuth } from '../../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Workflow type options with descriptions
const WORKFLOW_TYPES = [
  { value: 'TAX_SEARCH', label: 'Tax Search', description: 'Tax Team & Processors', color: 'bg-blue-100 text-blue-700' },
  { value: 'TITLE_SEARCH', label: 'Title Search', description: 'Title Team & Search Processors', color: 'bg-purple-100 text-purple-700' },
  { value: 'MLS', label: 'MLS', description: 'MLS Products (Tax Team)', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'UTILITY', label: 'Utility', description: 'HOA & Utility (Tax Team)', color: 'bg-teal-100 text-teal-700' },
  { value: 'OTHER', label: 'Other', description: 'Miscellaneous', color: 'bg-slate-100 text-slate-700' },
];

// Category options
const CATEGORIES = [
  'Tax Searches',
  'MLS Products',
  'Utility and HOA',
  'Owner Searches',
  'Comprehensive Searches',
  'Document Searches',
  'Legal and Title',
  'Commercial Products',
  'Other'
];

const WorkflowTypeBadge = ({ type }) => {
  const config = WORKFLOW_TYPES.find(w => w.value === type) || WORKFLOW_TYPES[4];
  return (
    <Badge className={cn("font-medium", config.color)}>
      {config.label}
    </Badge>
  );
};

const ProductManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Accounting gets a read-only view — backend write endpoints are admin-only.
  const canManage = user?.role === 'admin';
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const { searchInput: searchQuery, setSearchInput: setSearchQuery, debouncedValue: debouncedSearch } = useDebouncedSearch('', 250);
  const [filterWorkflow, setFilterWorkflow] = useState('all');
  const [includeInactive, setIncludeInactive] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    workflow_type: 'OTHER',
    category: 'Other',
    default_price: '',
    notes: '',
    active: true,
    orderable: true
  });

  // Sync Product Catalog state
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const handleSyncCatalog = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const response = await axios.post(`${API}/products/seed`);
      const data = response.data;
      setSyncResult({
        success: true,
        created: data.inserted?.length || 0,
        skipped: data.skipped?.length || 0,
        createdNames: data.inserted || [],
        skippedNames: data.skipped || [],
        message: data.message
      });
      toast.success(`Catalog synced: ${data.inserted?.length || 0} created, ${data.skipped?.length || 0} already existed`);
      fetchProducts();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to sync product catalog';
      setSyncResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setSyncing(false);
    }
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('include_inactive', includeInactive.toString());
      if (filterWorkflow !== 'all') {
        params.append('workflow_type', filterWorkflow);
      }
      
      const response = await axios.get(`${API}/products/all?${params.toString()}`);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [includeInactive, filterWorkflow]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const resetForm = () => {
    setFormData({
      name: '',
      workflow_type: 'OTHER',
      category: 'Other',
      default_price: '',
      notes: '',
      active: true,
      orderable: true
    });
    setEditingProduct(null);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      workflow_type: product.workflow_type || 'OTHER',
      category: product.category || 'Other',
      default_price: product.default_price?.toString() || '',
      notes: product.notes || '',
      active: product.active ?? true,
      orderable: product.orderable ?? true
    });
    setShowDialog(true);
  };

  const handleCreateProduct = async () => {
    if (!formData.name.trim()) {
      toast.error('Product name is required');
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        workflow_type: formData.workflow_type,
        category: formData.category,
        default_price: formData.default_price ? parseFloat(formData.default_price) : null,
        notes: formData.notes || null,
        active: formData.active,
        orderable: formData.orderable
      };

      await axios.post(`${API}/client-products`, payload);
      toast.success('Product created successfully');
      setShowDialog(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error creating product:', error);
      const msg = error.response?.data?.detail || 'Failed to create product';
      toast.error(msg);
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;

    try {
      const payload = {
        name: formData.name.trim(),
        workflow_type: formData.workflow_type,
        category: formData.category,
        default_price: formData.default_price ? parseFloat(formData.default_price) : null,
        notes: formData.notes || null,
        active: formData.active,
        orderable: formData.orderable
      };

      await axios.put(`${API}/client-products/${editingProduct.id}`, payload);
      toast.success('Product updated successfully');
      setShowDialog(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error updating product:', error);
      const msg = error.response?.data?.detail || 'Failed to update product';
      toast.error(msg);
    }
  };

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;

    try {
      await axios.delete(`${API}/client-products/${productToDelete.id}`);
      toast.success('Product deactivated successfully');
      setShowDeleteConfirm(false);
      setProductToDelete(null);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      const msg = error.response?.data?.detail || 'Failed to delete product';
      toast.error(msg);
    }
  };

  // Filter products by search query
  const filteredProducts = products.filter(p => 
    p.name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    p.category?.toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  return (
    <div className="min-h-full bg-gradient-to-br from-slate-50 via-white to-slate-50" data-testid="product-management">
      {/* Sticky Header with Navigation */}
      <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-[1400px] mx-auto px-6 py-4">
          {/* Breadcrumb Navigation */}
          <div className="flex items-center gap-2 text-sm mb-3">
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-1.5 text-slate-500 hover:text-slate-900 transition-colors"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Admin</span>
            </button>
            <span className="text-slate-300">/</span>
            <button
              onClick={() => navigate('/admin/billing')}
              className="text-slate-500 hover:text-slate-900 transition-colors"
              data-testid="back-to-billing-btn"
            >
              Billing
            </button>
            <span className="text-slate-300">/</span>
            <span className="text-slate-900 font-medium">Product Master</span>
          </div>
          
          {/* Title and Action */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                <Package className="w-6 h-6 text-blue-600" />
                Product Master
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Manage products, workflow assignments, and default pricing
              </p>
            </div>
            
            {/* Quick Stats */}
            <div className="hidden md:flex items-center gap-4">
              <div className="text-right">
                <p className="text-xs text-slate-400 uppercase tracking-wider">Total Products</p>
                <p className="text-lg font-semibold text-slate-700">{products.length}</p>
              </div>
              <div className="h-8 w-px bg-slate-200" />
              <div className="text-right">
                <p className="text-xs text-slate-400 uppercase tracking-wider">Active</p>
                <p className="text-lg font-semibold text-emerald-600">{products.filter(p => p.active).length}</p>
              </div>
              <div className="h-8 w-px bg-slate-200" />
              {canManage && (
                <>
                  <Button 
                    variant="outline"
                    onClick={() => { setSyncResult(null); setShowSyncConfirm(true); }}
                    className="gap-2 border-amber-200 text-amber-700 hover:bg-amber-50 hover:border-amber-300"
                    data-testid="sync-catalog-btn"
                  >
                    <DatabaseZap className="w-4 h-4" />
                    Sync Catalog
                  </Button>
                  <Button 
                    onClick={() => { resetForm(); setShowDialog(true); }}
                    className="bg-blue-600 hover:bg-blue-700 gap-2"
                    data-testid="add-product-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Add Product
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-6 py-6 space-y-6">
        {/* Mobile Add Button */}
        {canManage && (
          <div className="md:hidden">
            <Button 
              onClick={() => { resetForm(); setShowDialog(true); }}
              className="w-full bg-blue-600 hover:bg-blue-700 gap-2"
              data-testid="add-product-btn-mobile"
            >
              <Plus className="w-4 h-4" />
              Add Product
            </Button>
          </div>
        )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="product-search-input"
                />
              </div>
            </div>
            
            <Select value={filterWorkflow} onValueChange={setFilterWorkflow}>
              <SelectTrigger className="w-[180px]" data-testid="workflow-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Workflow Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Workflows</SelectItem>
                {WORKFLOW_TYPES.map(wt => (
                  <SelectItem key={wt.value} value={wt.value}>{wt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="rounded border-slate-300"
              />
              Include Inactive
            </label>
            
            <Button variant="outline" size="sm" onClick={fetchProducts} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Products ({filteredProducts.length})</CardTitle>
          <CardDescription>
            Products determine what services are offered and which teams handle them
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading products...</div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No products found. Click "Add Product" to create one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="tf-thead">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">Product Name</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">Workflow Type</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">Category</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">Default Price</th>
                    <th className="text-center py-3 px-4 font-semibold text-slate-600">Status</th>
                    <th className="text-center py-3 px-4 font-semibold text-slate-600">Usage</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map(product => (
                    <tr 
                      key={product.id} 
                      className={cn(
                        "border-b border-slate-100 hover:bg-slate-50 transition-colors",
                        !product.active && "opacity-60 bg-slate-50"
                      )}
                      data-testid={`product-row-${product.id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-800">{product.name}</div>
                        {product.notes && (
                          <div className="text-xs text-slate-500 mt-0.5">{product.notes}</div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <WorkflowTypeBadge type={product.workflow_type} />
                      </td>
                      <td className="py-3 px-4 text-slate-600">
                        {product.category || '—'}
                      </td>
                      <td className="py-3 px-4">
                        {product.default_price ? (
                          <span className="font-medium text-emerald-600">${product.default_price.toFixed(2)}</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {product.active ? (
                          <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">Inactive</Badge>
                        )}
                        {product.orderable !== false ? (
                          <Badge className="bg-blue-100 text-blue-700 ml-1">Orderable</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-500 ml-1">Not Orderable</Badge>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex flex-col items-center gap-1 text-xs">
                          <span className="text-slate-500">{product.orders_count || 0} orders</span>
                          <span className="text-slate-500">{product.pricing_rules_count || 0} pricing rules</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {canManage ? (
                        <div className="flex items-center justify-end gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => openEditDialog(product)}
                                  className="h-8 w-8 p-0 text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                                  data-testid={`edit-product-${product.id}`}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit Product</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          
                          {product.active && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => handleDeleteClick(product)}
                                    className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                                    data-testid={`delete-product-${product.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Deactivate Product</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        ) : (
                          <div className="flex justify-end">
                            <span className="text-xs text-slate-400" data-testid={`readonly-product-${product.id}`}>Read-only</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowDialog(open); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingProduct ? <Edit2 className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </DialogTitle>
            <DialogDescription>
              {editingProduct 
                ? 'Update product details and workflow assignment.'
                : 'Create a new product in the Product Master.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Product Name */}
            <div className="space-y-2">
              <Label>Product Name <span className="text-red-500">*</span></Label>
              <Input 
                placeholder="e.g., Tax Search, Full Search"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                maxLength={30}
                data-testid="product-name-input"
              />
              <p className="text-xs text-slate-500">{formData.name.length}/30 characters</p>
            </div>
            
            {/* Workflow Type */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Workflow Type <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={formData.workflow_type} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, workflow_type: value }))}
              >
                <SelectTrigger data-testid="workflow-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WORKFLOW_TYPES.map(wt => (
                    <SelectItem key={wt.value} value={wt.value}>
                      <div className="flex items-center gap-2">
                        <span>{wt.label}</span>
                        <span className="text-xs text-slate-400">({wt.description})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Determines which team/role handles orders for this product
              </p>
            </div>
            
            {/* Category */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Category
              </Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Default Price */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Default Price (optional)
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500">$</span>
                <Input 
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.default_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, default_price: e.target.value }))}
                  className="pl-7"
                  data-testid="default-price-input"
                />
              </div>
              <p className="text-xs text-slate-500">
                System fallback price when no client-specific pricing rule exists
              </p>
            </div>
            
            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input 
                placeholder="Internal notes about this product"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
            
            {/* Active Toggle (only for edit) */}
            {editingProduct && (
              <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Product Status</Label>
                  <p className="text-xs text-slate-500">
                    {formData.active 
                      ? 'Active products appear in dropdowns and can be used'
                      : 'Inactive products are hidden from selection'}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.active}
                  onClick={() => setFormData(prev => ({ ...prev, active: !prev.active }))}
                  className={cn(
                    "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors",
                    formData.active ? "bg-emerald-500" : "bg-slate-300"
                  )}
                  data-testid="product-active-toggle"
                >
                  <span
                    className={cn(
                      "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition-transform",
                      formData.active ? "translate-x-5" : "translate-x-0"
                    )}
                  />
                </button>
              </div>
            )}

            {/* Orderable Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">Orderable</Label>
                <p className="text-xs text-slate-500">
                  {formData.orderable
                    ? 'Product appears in Create New Order picker'
                    : 'Product is hidden from Create New Order picker'}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={formData.orderable}
                onClick={() => setFormData(prev => ({ ...prev, orderable: !prev.orderable }))}
                className={cn(
                  "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors",
                  formData.orderable ? "bg-blue-500" : "bg-slate-300"
                )}
                data-testid="product-orderable-toggle"
              >
                <span
                  className={cn(
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition-transform",
                    formData.orderable ? "translate-x-5" : "translate-x-0"
                  )}
                />
              </button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setShowDialog(false); }}>
              Cancel
            </Button>
            <Button 
              onClick={editingProduct ? handleUpdateProduct : handleCreateProduct}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-product-btn"
            >
              {editingProduct ? 'Update Product' : 'Create Product'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Deactivate Product
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to deactivate "{productToDelete?.name}"?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {productToDelete?.orders_count > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <strong>Note:</strong> This product is used in {productToDelete.orders_count} order(s). 
                It will be soft-deleted and hidden from future selection, but existing orders will retain the reference.
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleConfirmDelete}
              data-testid="confirm-delete-btn"
            >
              Deactivate Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync Product Catalog Confirmation Dialog */}
      <Dialog open={showSyncConfirm} onOpenChange={(open) => { if (!syncing) setShowSyncConfirm(open); }}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <DatabaseZap className="w-5 h-5" />
              Sync Product Catalog
            </DialogTitle>
            <DialogDescription>
              This will ensure all standard products exist in your Product Master.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-3 space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <p className="font-medium mb-1">What this does:</p>
              <ul className="list-disc list-inside space-y-0.5 text-blue-700">
                <li>Inserts any missing canonical products</li>
                <li>Skips products that already exist (by name)</li>
                <li>Does NOT modify or overwrite existing products</li>
                <li>Does NOT affect pricing rules or order history</li>
              </ul>
            </div>

            {syncResult && (
              <div className={cn(
                "border rounded-lg p-3 text-sm",
                syncResult.success 
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800" 
                  : "bg-red-50 border-red-200 text-red-800"
              )} data-testid="sync-result">
                {syncResult.success ? (
                  <>
                    <p className="font-medium mb-2 flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4" /> Sync Complete
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-white/60 rounded p-2">
                        <span className="font-semibold text-emerald-700 text-base">{syncResult.created}</span>
                        <span className="block text-emerald-600">Created</span>
                      </div>
                      <div className="bg-white/60 rounded p-2">
                        <span className="font-semibold text-slate-700 text-base">{syncResult.skipped}</span>
                        <span className="block text-slate-500">Already Existed</span>
                      </div>
                    </div>
                    {syncResult.createdNames.length > 0 && (
                      <div className="mt-2 text-xs">
                        <span className="font-medium">New products: </span>
                        {syncResult.createdNames.join(', ')}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="flex items-center gap-1.5">
                    <XCircle className="w-4 h-4" /> {syncResult.message}
                  </p>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSyncConfirm(false)} disabled={syncing}>
              {syncResult?.success ? 'Close' : 'Cancel'}
            </Button>
            {!syncResult?.success && (
              <Button 
                onClick={handleSyncCatalog}
                disabled={syncing}
                className="bg-amber-600 hover:bg-amber-700 gap-2"
                data-testid="confirm-sync-btn"
              >
                {syncing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <DatabaseZap className="w-4 h-4" />
                    Confirm Sync
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductManagement;
